import axios from "axios";

/**
 * Crop Progression (Vegetation Vigor) API service.
 *
 * Talks only to the Node backend (base URL already includes /api/admin). The
 * backend proxies the Python crop-monitoring service, so the frontend stays
 * isolated from it. Follows the project _services convention: methods resolve
 * with the response data / reject with error.response.
 */
const BASE = "crop-progression";

export default {
  // Available satellite dates for a geofence (proxied Python /dates/)
  async getAvailableDates(payload) {
    try {
      const { data } = await axios.post(`${BASE}/available-dates`, payload);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Index metadata (descriptions, scale notes, colorbar image urls)
  async getIndicesMetadata() {
    try {
      const { data } = await axios.get(`${BASE}/indices-metadata`);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Guide content (index metadata + supported indices)
  async getGuide() {
    try {
      const { data } = await axios.get(`${BASE}/guide`);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Generate a report (create + enqueue background generation)
  async generateReport(payload) {
    try {
      const { data } = await axios.post(`${BASE}`, payload);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Paginated list
  async listReports(params) {
    try {
      const { data } = await axios.get(`${BASE}`, { params });
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Single report detail (poll this until status !== GENERATING)
  async getReport(reportUid) {
    try {
      const { data } = await axios.get(`${BASE}/${reportUid}`);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Available dates for this report's geofence + which dates already generated
  async getRelatedDates(reportUid) {
    try {
      const { data } = await axios.get(`${BASE}/${reportUid}/related-dates`);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Comparison — ensure reports exist for the given dates (generate missing)
  async compareReports(payload) {
    try {
      const { data } = await axios.post(`${BASE}/compare`, payload);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  async deleteReport(reportUid) {
    try {
      const { data } = await axios.delete(`${BASE}/${reportUid}`);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  async submitFeedback(reportUid, payload) {
    try {
      const { data } = await axios.post(`${BASE}/${reportUid}/feedback`, payload);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  async getFeedback(reportUid) {
    try {
      const { data } = await axios.get(`${BASE}/${reportUid}/feedback`);
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Returns a PDF blob (all index images grouped into one document)
  async downloadPdf(reportUid) {
    try {
      const res = await axios.post(
        `${BASE}/${reportUid}/download-pdf`,
        {},
        { responseType: "blob" }
      );
      return Promise.resolve(res);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },

  // Returns a comparison PDF blob (two dates' index images side by side)
  async downloadComparisonPdf({ reportUidA, reportUidB }) {
    try {
      const res = await axios.post(
        `${BASE}/compare/download-pdf`,
        { reportUidA, reportUidB },
        { responseType: "blob" }
      );
      return Promise.resolve(res);
    } catch (error) {
      return Promise.reject(error.response);
    }
  },
};
